/**
 * Trust Score — оценка достоверности осмотра.
 *
 * CLAUDE.md, правило 17: водителю не показывается НИКОГДА. Как только человек
 * видит число, он начинает оптимизировать его, а не проходить осмотр.
 * Менеджеру показывается всегда с причинами: число без объяснения —
 * это обвинение, с объяснением — повод задать вопрос.
 *
 * Низкий балл НЕ блокирует осмотр. Эвристика ошибается: водитель мог законно
 * осматривать машину в другом дворе.
 */
import { z } from 'zod';
export const TRUST_SIGNALS = [
    'duplicate_photo',
    'missing_exif',
    'capture_upload_gap',
    'clock_skew',
    'too_fast',
    'no_gps_drift',
    'outside_geofence',
    'no_gps',
];
export const SIGNAL_SPECS = {
    duplicate_photo: {
        signal: 'duplicate_photo',
        penalty: 35,
        label: 'Repeated photos',
        explanation: 'The component photo visually matches a photo from an earlier inspection.',
    },
    outside_geofence: {
        signal: 'outside_geofence',
        // Вес подобран так, чтобы САМ ПО СЕБЕ сигнал не понижал оценку:
        // предрейсовый осмотр на стоянке вместо двора — норма для дальнобоя.
        // Значение он приобретает только в сочетании с другими сигналами.
        penalty: 20,
        label: 'Outside the yard',
        explanation: 'The inspection was done far from the site this unit belongs to.',
    },
    too_fast: {
        signal: 'too_fast',
        penalty: 20,
        label: 'Too fast',
        explanation: 'Average time per item was less than inspecting a component takes.',
    },
    no_gps_drift: {
        signal: 'no_gps_drift',
        penalty: 15,
        label: 'No movement',
        explanation: 'All photos were taken from one spot, though walking around the vehicle requires moving.',
    },
    clock_skew: {
        signal: 'clock_skew',
        penalty: 15,
        label: 'Clock drift',
        explanation: 'The device time differs noticeably from the server time.',
    },
    missing_exif: {
        signal: 'missing_exif',
        penalty: 10,
        label: 'No EXIF',
        explanation: 'The photo has no capture metadata.',
    },
    capture_upload_gap: {
        signal: 'capture_upload_gap',
        penalty: 10,
        label: 'Stale photo',
        explanation: 'An unusually long time passed between capture and submission.',
    },
    no_gps: {
        signal: 'no_gps',
        penalty: 5,
        label: 'No location',
        explanation: 'Geolocation is unavailable or turned off.',
    },
};
export const trustVerdictSchema = z.enum(['trusted', 'review', 'suspect']);
/** Пороги словесной оценки. Число без слова читается хуже, чем слово без числа. */
export function trustVerdict(score) {
    if (score >= 80)
        return 'trusted';
    if (score >= 50)
        return 'review';
    return 'suspect';
}
export function scoreTrust(triggered) {
    // Каждый сигнал учитывается один раз, даже если сработал на нескольких пунктах.
    const unique = new Map(triggered.map((t) => [t.signal, t]));
    const penalty = [...unique.values()].reduce((sum, t) => sum + SIGNAL_SPECS[t.signal].penalty, 0);
    const score = Math.max(0, 100 - penalty);
    return { score, verdict: trustVerdict(score), signals: [...unique.values()] };
}
/* ---------------------------- пороги сигналов ----------------------------- */
/** Минимум секунд на пункт, ниже которого осмотр физически невозможен. */
export const MIN_SECONDS_PER_ITEM = 8;
/** Радиус вокруг площадки, за которым осмотр считается выполненным не во дворе. */
export const GEOFENCE_SLACK_METERS = 1500;
/** Смещение между снимками, ожидаемое при обходе машины. */
export const MIN_GPS_DRIFT_METERS = 3;
/** Разрыв между съёмкой и отправкой, после которого снимок считается старым. */
export const MAX_CAPTURE_GAP_MS = 12 * 3_600_000;
/** Расстояние Хэмминга между dHash, ниже которого снимки считаются одинаковыми. */
export const DUPLICATE_HAMMING_THRESHOLD = 6;
/** Расстояние между точками по формуле гаверсинуса, в метрах. */
export function distanceMeters(a, b) {
    const R = 6_371_000;
    const toRad = (deg) => (deg * Math.PI) / 180;
    const dLat = toRad(b.lat - a.lat);
    const dLon = toRad(b.lon - a.lon);
    const lat1 = toRad(a.lat);
    const lat2 = toRad(b.lat);
    const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
    return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}
/** Расстояние Хэмминга между шестнадцатеричными хешами одинаковой длины. */
export function hammingDistance(a, b) {
    if (a.length !== b.length)
        return Number.POSITIVE_INFINITY;
    let distance = 0;
    for (let i = 0; i < a.length; i += 1) {
        const diff = parseInt(a[i] ?? '0', 16) ^ parseInt(b[i] ?? '0', 16);
        distance += (diff & 1) + ((diff >> 1) & 1) + ((diff >> 2) & 1) + ((diff >> 3) & 1);
    }
    return distance;
}
//# sourceMappingURL=trust.js.map